import { useIsRibbonRail } from '@/components/layout/Navigation/hooks/useIsRibbonRail';
import { NavItem } from '@/components/layout/Navigation/sidebar/nav-item';
import { useIsSidebarIAEnabled } from '@/features/flags';
import { SidebarMenu } from '@heidi/design-system/page-shell';

import {
  useFooterRailCollapsible,
  type FooterMoreRowDescriptor,
} from './footer-rail-context';
import { FOOTER_RAIL_DISPLAY_ORDER, type FooterRailId } from './footer-rail.constants';

interface FooterRailCollapsibleItemProps {
  /** Base eligibility — whether the item shows at all, in any sidebar mode. */
  shouldShow: boolean;
  descriptor: FooterMoreRowDescriptor;
  /** When true, item never collapses into More (treated like Help/Notifications). */
  pinned?: boolean;
}

/**
 * Renders a footer collapsible (Get Started, Evidence Referral) that would
 * otherwise be inlined in SideNavFooter. It has to be a child of
 * FooterRailProvider: registering from SideNavFooter itself reads the registry
 * context from *above* the provider that same component renders — the default
 * noop registry — so the item silently never registers and never collapses.
 */
export function FooterRailCollapsibleItem({
  shouldShow,
  descriptor,
  pinned = false,
}: FooterRailCollapsibleItemProps) {
  const isRibbonRail = useIsRibbonRail();
  const isSidebarIAEnabled = useIsSidebarIAEnabled();

  // Fix 10: guard the unchecked cast — a stale or unknown id must not register
  // an untracked rail entry. Keep the hook call unconditional (Rules of Hooks)
  // by passing a valid fallback id and setting visible to false.
  const railId = FOOTER_RAIL_DISPLAY_ORDER.includes(descriptor.id as FooterRailId)
    ? (descriptor.id as FooterRailId)
    : undefined;

  const isCollapsed = useFooterRailCollapsible({
    id: railId ?? 'refer',
    visible: !!railId && shouldShow && isRibbonRail,
    pinned,
    descriptor,
  });

  if (!railId || !shouldShow || isCollapsed) return null;

  const item = (
    <NavItem
      label={descriptor.label}
      icon={descriptor.icon}
      href={descriptor.href}
      external={descriptor.external}
      variant="emphasis"
      layout={isSidebarIAEnabled && isRibbonRail ? 'iconOnly' : 'default'}
      onClick={descriptor.onSelect}
      testId={descriptor.testId}
    />
  );

  // Ribbon rail: bare item — the shared footer <ul> is owned by SideNavFooter.
  return isRibbonRail ? item : <SidebarMenu>{item}</SidebarMenu>;
}
